import { ApiLoginResponse, AuthResponse } from "../types/auth";
import { buildAppPath } from "../config/appBasePath";
import { runtimeConfig } from "../config/runtimeConfig";

const TOKEN_KEY = "supplier_access_token";
const USER_KEY = "supplier_user_data";

const API_URL = runtimeConfig.adminApiUrl;

/**
 * Supplier module ID in the new backend.
 * Modules column stores a comma-separated list: "1,2,3"
 *   1 = admin, 2 = client, 3 = supplier
 */
const SUPPLIER_MODULE_ID = "3";

function isSupplierAllowed(modules?: string | null): boolean {
    if (!modules) return false;
    return modules.split(",").map((s) => s.trim()).includes(SUPPLIER_MODULE_ID);
}

function mapLoginResponse(raw: ApiLoginResponse): AuthResponse {
    const user = raw.user;
    return {
        accessToken: raw.tokenValue,
        userId: String(user.id),
        username: user.username,
        fullname: user.fullname,
        modules: user.modules,
        supplierId: String(user.supplierId ?? ""),
        supplierName: user.supplierName ?? "",
    };
}

export const supplierAuthService = {
    getAccessToken: () => localStorage.getItem(TOKEN_KEY),

    redirectToLogin: () => {
        window.location.assign(buildAppPath("/login"));
    },

    getUserData: (): AuthResponse | null => {
        const data = localStorage.getItem(USER_KEY);
        if (!data) return null;
        try {
            return JSON.parse(data) as AuthResponse;
        } catch {
            return null;
        }
    },

    /** Get the stored user or throw – use when you know the user is logged in. */
    getUser: (): AuthResponse => {
        const user = supplierAuthService.getUserData();
        if (!user) throw new Error("Not logged in.");
        return user;
    },

    /** Shortcut: returns the logged-in user's ID as a number (throws if not logged in). */
    getUserId: (): number => {
        const id = parseInt(supplierAuthService.getUser().userId, 10);
        if (isNaN(id) || id <= 0) throw new Error("Invalid user ID in session.");
        return id;
    },

    setSession: (response: AuthResponse) => {
        localStorage.setItem(TOKEN_KEY, response.accessToken);
        localStorage.setItem(USER_KEY, JSON.stringify(response));
    },

    clearSession: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    },

    login: async (credentials: { username: string; password: string }): Promise<AuthResponse> => {
        const res = await fetch(`${API_URL}/Login/Login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                username: credentials.username,
                password: credentials.password,
                module: SUPPLIER_MODULE_ID,
            }),
            credentials: "include",
        });

        if (!res.ok) {
            const fallbackMessage = res.status === 404
                ? "Invalid username or password."
                : "Login failed";
            const payload = await res.json().catch(() => ({}));
            throw new Error((payload as any).message || fallbackMessage);
        }

        const raw: ApiLoginResponse = await res.json();
        const data = mapLoginResponse(raw);

        if (!isSupplierAllowed(data.modules)) {
            throw new Error("Access denied: your account does not have Supplier access.");
        }

        supplierAuthService.setSession(data);
        return data;
    },

    logout: async () => {
        supplierAuthService.clearSession();
        supplierAuthService.redirectToLogin();
    },

    /**
     * The new backend does not support refresh tokens.
     * Clear the session and redirect to login.
     */
    refreshToken: async (): Promise<AuthResponse> => {
        supplierAuthService.clearSession();
        supplierAuthService.redirectToLogin();
        throw new Error("Session expired. Please log in again.");
    },
};
